import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Popover,
  List,
  ListItem,
  Badge,
  alpha,
  useTheme,
  CircularProgress,
  Button,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import {
  NotificationsNoneOutlined,
  WarningOutlined,
  LocalShippingOutlined,
  CampaignOutlined,
  InfoOutlined,
} from "@mui/icons-material";
import notificationService from "../services/notificationService";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router";
import {
  NotificationType,
  PRIORITY_OPTIONS,
} from "../constants/notificationType";
import { WsEvents } from "../constants/wsEvents";
import { AppRoutes } from "../constants/appRoutes";
import { AppConfig } from "../constants/appConfig";
import { logger } from "../utils/logger";

export const NotificationCenter = () => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const navigate = useNavigate();

  const { isAuthenticated, accessToken } = useAuth();

  useEffect(() => {
    let ws = null;
    let reconnectTimeout = null;
    let pollInterval = null;
    let reconnectAttempts = 0;
    let isEffectActive = true;

    const connectWebSocket = () => {
      if (!isAuthenticated || !isEffectActive) return;

      const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsUrl =
        AppConfig.ENV.API_URL?.replace(/^https?:/, wsProtocol) +
        `/notifications/ws?project=jigenpass-b2b${accessToken ? `&token=${accessToken}` : ""}`;

      logger.debug(
        `[WebSocket] Connecting to real-time notification service...`,
      );
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        if (!isEffectActive) {
          ws.close();
          return;
        }
        logger.debug("[WebSocket] Connected to real-time notification service");
        reconnectAttempts = 0;
      };

      ws.onmessage = (event) => {
        if (!isEffectActive) return;
        try {
          const payload = JSON.parse(event.data);
          if (payload.type === WsEvents.PING) {
            ws.send(JSON.stringify({ type: "pong" }));
          } else if (payload.type === WsEvents.NOTIFICATION) {
            setNotifications((prev) => [payload.data, ...prev].slice(0, 10));
            setUnreadCount((prev) => prev + 1);
          }
        } catch (e) {
          logger.error("[WebSocket] Failed to parse payload", {
            error: e.message,
          });
        }
      };

      ws.onerror = () => {
        logger.warn("[WebSocket] Connection error — will retry");
      };

      ws.onclose = (event) => {
        if (!isEffectActive) return;

        const shouldReconnect =
          !event.wasClean ||
          event.code === 1001 ||
          event.code === 1008 ||
          event.code === 1011;

        if (shouldReconnect) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
          logger.debug(
            `[WebSocket] Closed (code ${event.code}). Retrying in ${delay / 1000}s...`,
          );
          reconnectTimeout = setTimeout(() => {
            reconnectAttempts++;
            connectWebSocket();
          }, delay);
        } else {
          logger.debug("[WebSocket] Connection closed cleanly");
        }
      };
    };

    if (isAuthenticated) {
      fetchNotifications();
      connectWebSocket();
      pollInterval = setInterval(fetchNotifications, 120000);
    }

    return () => {
      isEffectActive = false;
      if (pollInterval) clearInterval(pollInterval);
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws) ws.close();
    };
  }, [isAuthenticated]);

  const fetchNotifications = async () => {
    try {
      const list = await notificationService.listNotifications({ limit: 8 });
      setNotifications(list);
      setUnreadCount(list.filter((n) => !n.isRead).length);
    } catch (err) {
      console.error(err);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (n) => {
    if (!n.isRead) {
      try {
        await notificationService.markAsRead(n.id);
      } catch (err) {
        console.warn("Failed to mark notification as read:", err.message);
      }
      fetchNotifications();
    }

    handleClose();

    const data = n.data || {};

    if (
      data.invoiceNumber ||
      n.title.toLowerCase().includes("invoice") ||
      n.title.toLowerCase().includes("payment") ||
      n.title.toLowerCase().includes("dunning")
    ) {
      navigate(AppRoutes.FINANCE);
    } else if (data.quoteNumber || n.title.toLowerCase().includes("quote")) {
      navigate(AppRoutes.QUOTES_1);
    } else if (
      data.preOrderNumber ||
      n.title.toLowerCase().includes("pre-order") ||
      n.title.toLowerCase().includes("preorder") ||
      data.isPreOrder
    ) {
      navigate(AppRoutes.PRE_ORDERS_1);
    } else if (
      data.trackingNumber ||
      n.title.toLowerCase().includes("shipment") ||
      n.type === "SHIPMENT"
    ) {
      navigate(AppRoutes.ORDERS_1);
    } else if (
      data.rmaId ||
      n.title.toLowerCase().includes("rma") ||
      n.type === "RMA"
    ) {
      navigate(AppRoutes.ORDERS_1);
    } else if (
      data.orderNumber ||
      data.orderId ||
      n.type === NotificationType.ORDER_STATUS
    ) {
      navigate(AppRoutes.ORDERS_1);
    }
  };

  return (
    <>
      <IconButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        size="small"
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1.5,
          p: 1,
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          variant={unreadCount > 0 ? "standard" : "dot"}
        >
          <NotificationsNoneOutlined
            sx={{ color: unreadCount > 0 ? "primary.main" : "text.secondary" }}
          />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            width: 380,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              color="primary"
              onClick={() =>
                notificationService.markAllAsRead().then(fetchNotifications)
              }
            >
              Mark All Read
            </Button>
          )}
        </Box>
        <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <CircularProgress color="primary" size={24} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">
                You're all caught up!
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notifications.map((n) => (
                <ListItem
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  sx={{
                    cursor: "pointer",
                    p: 2,
                    bgcolor: n.isRead
                      ? "transparent"
                      : alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
                    <Box
                      sx={{
                        color:
                          n.type === NotificationType.ALERT
                            ? "error.main"
                            : n.type === NotificationType.INFO
                              ? "info.main"
                              : "primary.main",
                        mt: 0.5,
                      }}
                    >
                      {n.type === NotificationType.ALERT ? (
                        <WarningOutlined />
                      ) : n.type === NotificationType.ORDER_STATUS ? (
                        <LocalShippingOutlined />
                      ) : n.type === NotificationType.PROMOTION ? (
                        <CampaignOutlined />
                      ) : n.type === NotificationType.INFO ? (
                        <InfoOutlined />
                      ) : (
                        <NotificationsNoneOutlined />
                      )}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{
                          color: "text.primary",
                          fontWeight: n.isRead ? 500 : 700,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {n.title}
                        {n.priority && (
                          <Chip
                            label={n.priority.toUpperCase()}
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: "0.65rem",
                              fontWeight: 800,
                              color:
                                PRIORITY_OPTIONS.find(
                                  (p) => p.value === n.priority,
                                )?.color || "text.secondary",
                              bgcolor:
                                PRIORITY_OPTIONS.find(
                                  (p) => p.value === n.priority,
                                )?.bgColor || "action.hover",
                            }}
                          />
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          mt: 0.5,
                        }}
                      >
                        {n.message}
                      </Typography>
                    </Box>
                  </Stack>
                </ListItem>
              ))}
            </List>
          )}

          <Divider />
          <Box sx={{ p: 1, textAlign: "center" }}>
            <Button
              fullWidth
              size="small"
              onClick={() => {
                setAnchorEl(null);
                navigate(AppRoutes.NOTIFICATIONS);
              }}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            >
              View All Notifications
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
